import * as React from 'react';
import {
    Checkbox,
    ChoiceGroup,
    DefaultButton,
    Dropdown,
    IDropdownOption,
    IconButton,
    Label,
    MessageBar,
    MessageBarType,
    PrimaryButton,
    Spinner,
    SpinnerSize,
    Stack,
    Text,
    TextField,
} from '@fluentui/react';
import {
    AGENCY_SPEC_FIELD_DESCRIPTORS,
    AGENCY_SPEC_REFERENCE_FORMAT_OPTIONS,
    AGENCY_ARTIFACT_RESOLUTION_TYPE_OPTIONS,
    ARTIFACT_TYPE_OPTIONS,
    AgencyModelPickerItem,
    AgencyArtifactResolutionType,
    AgentArtifactEditorState,
    ArtifactEditorState,
    ArtifactSummary,
    ExtractionArtifactEditorState,
    JOB_RESULT_EXTRACTION_DESTINATION_OPTIONS,
    JOB_RESULT_EXTRACTION_MODE_OPTIONS,
    JOB_RESULT_EXTRACTION_PURPOSE_OPTIONS,
    JOB_RESULT_EXTRACTION_SOURCE_OPTIONS,
    JobDefinitionEditorState,
    PipelineArtifactEditorState,
    PluginArtifactEditorState,
    RuntimeSubstitutionOption,
    TASK_TYPES,
    getArtifactTypeLabel,
} from '../../shared';
import { AgencyModelConfigurationEditor } from '../../AgencyModelConfigurationEditor';
import { DefinitionRelationshipsEditor } from './DefinitionRelationshipsEditor';
import { SnippetTools } from './SnippetTools';

interface DefinitionEditorProps {
    mode: 'create' | 'edit';
    selectedName: string;
    isJobSection: boolean;
    isSnippetSection: boolean;
    isAgentSection: boolean;
    isPluginSection: boolean;
    isPipelineSection: boolean;
    isExtractionSection: boolean;
    hasSnippetPreview: boolean;
    previewLoading: boolean;
    saving: boolean;
    detailLoading: boolean;
    detailError: string | null;
    saveError: string | null;
    saveSuccess: string | null;
    previewError: string | null;
    snippetValidationError: string | null;
    jobForm: JobDefinitionEditorState;
    artifactForm: ArtifactEditorState;
    agentForm: AgentArtifactEditorState;
    pluginForm: PluginArtifactEditorState;
    pipelineForm: PipelineArtifactEditorState;
    extractionForm: ExtractionArtifactEditorState;
    relatedArtifacts: ArtifactSummary[];
    extractionArtifacts: ArtifactSummary[];
    pluginArtifacts: ArtifactSummary[];
    agencyModelOptions: AgencyModelPickerItem[];
    agencyModelsLoading: boolean;
    agencyModelsMessage: string | null;
    relationshipOptions: IDropdownOption[];
    extractionRelationshipOptions: IDropdownOption[];
    pluginRelationshipOptions: IDropdownOption[];
    bundledPluginOptions: IDropdownOption[];
    relationshipToAddId?: number;
    extractionRelationshipToAddId?: number;
    pluginRelationshipToAddId?: number;
    selectedAgentOptions: IDropdownOption[];
    selectedPipelineOptions: IDropdownOption[];
    dependsOnOptions: IDropdownOption[];
    enabledSnippets: ArtifactSummary[];
    selectedSnippetName?: string;
    availableSubstitutions: RuntimeSubstitutionOption[];
    selectedSubstitutionToken?: string;
    selectedInsertionKind: 'snippet' | 'substitution';
    previewSourceText: string;
    previewText: string | null;
    pluginSpecPreview: string;
    pluginSpecWarning: string | null;
    editorRef: React.RefObject<HTMLTextAreaElement>;
    setJobForm: React.Dispatch<React.SetStateAction<JobDefinitionEditorState>>;
    setArtifactForm: React.Dispatch<React.SetStateAction<ArtifactEditorState>>;
    setAgentForm: React.Dispatch<React.SetStateAction<AgentArtifactEditorState>>;
    setPluginForm: React.Dispatch<React.SetStateAction<PluginArtifactEditorState>>;
    setPipelineForm: React.Dispatch<React.SetStateAction<PipelineArtifactEditorState>>;
    setExtractionForm: React.Dispatch<React.SetStateAction<ExtractionArtifactEditorState>>;
    onPreview: () => void;
    onOpenDuplicate: () => void;
    onSave: () => void;
    onRelationshipToAddIdChange: (artifactId?: number) => void;
    onAddRelationship: () => void;
    onRemoveRelationship: (artifactId: number) => void;
    onExtractionRelationshipToAddIdChange: (artifactId?: number) => void;
    onAddExtractionRelationship: () => void;
    onRemoveExtractionRelationship: (artifactId: number) => void;
    onPluginRelationshipToAddIdChange: (artifactId?: number) => void;
    onAddPluginRelationship: () => void;
    onRemovePluginRelationship: (artifactId: number) => void;
    onSelectedSnippetNameChange: (snippetName?: string) => void;
    onSelectedSubstitutionTokenChange: (token?: string) => void;
    onSelectedInsertionKindChange: (kind: 'snippet' | 'substitution') => void;
    onInsertSnippet: (snippetName?: string) => void;
    onInsertSubstitution: (token?: string) => void;
    onUpdateEditorText: (nextText: string, selectionStart?: number, selectionEnd?: number) => void;
}

export function DefinitionEditor(props: DefinitionEditorProps) {
    const {
        mode,
        selectedName,
        isJobSection,
        isSnippetSection,
        isAgentSection,
        isPluginSection,
        isPipelineSection,
        isExtractionSection,
        hasSnippetPreview,
        previewLoading,
        saving,
        detailLoading,
        detailError,
        saveError,
        saveSuccess,
        previewError,
        snippetValidationError,
        jobForm,
        artifactForm,
        agentForm,
        pluginForm,
        pipelineForm,
        extractionForm,
        relatedArtifacts,
        extractionArtifacts,
        pluginArtifacts,
        agencyModelOptions,
        agencyModelsLoading,
        agencyModelsMessage,
        relationshipOptions,
        extractionRelationshipOptions,
        pluginRelationshipOptions,
        bundledPluginOptions,
        relationshipToAddId,
        extractionRelationshipToAddId,
        pluginRelationshipToAddId,
        selectedAgentOptions,
        selectedPipelineOptions,
        dependsOnOptions,
        enabledSnippets,
        selectedSnippetName,
        availableSubstitutions,
        selectedSubstitutionToken,
        selectedInsertionKind,
        previewSourceText,
        previewText,
        pluginSpecPreview,
        pluginSpecWarning,
        editorRef,
        setJobForm,
        setArtifactForm,
        setAgentForm,
        setPluginForm,
        setPipelineForm,
        setExtractionForm,
        onPreview,
        onOpenDuplicate,
        onSave,
        onRelationshipToAddIdChange,
        onAddRelationship,
        onRemoveRelationship,
        onExtractionRelationshipToAddIdChange,
        onAddExtractionRelationship,
        onRemoveExtractionRelationship,
        onPluginRelationshipToAddIdChange,
        onAddPluginRelationship,
        onRemovePluginRelationship,
        onSelectedSnippetNameChange,
        onSelectedSubstitutionTokenChange,
        onSelectedInsertionKindChange,
        onInsertSnippet,
        onInsertSubstitution,
        onUpdateEditorText,
    } = props;

    const supportsTextEditing = isJobSection || (!isAgentSection && !isPluginSection && !isPipelineSection && !isExtractionSection);
    const supportsSnippetTools = isJobSection || (!isSnippetSection && !isAgentSection && !isPluginSection && !isPipelineSection && !isExtractionSection);
    const isAgencyBackend = jobForm.executionBackend === 0;
    const isAgencyAnalysisJob = isAgencyBackend && !jobForm.canCreatePR;
    const executionBackendOptions = [
        { key: '0', text: 'Agency' },
        { key: '1', text: 'CodeStar' },
    ];
    const specFormatOptions = AGENCY_SPEC_REFERENCE_FORMAT_OPTIONS.map(option => ({
        key: String(option.key),
        text: option.text,
    }));
    const getDefaultMaxConcurrencyForBackend = (executionBackend: number) => executionBackend === 1 ? 100 : 5;
    const pluginSpecFieldDescriptors = AGENCY_SPEC_FIELD_DESCRIPTORS[pluginForm.specFormat];
    const inlineChoiceGroupLabelStyles = {
        root: {
            margin: 0,
            padding: 0,
            minHeight: 32,
            display: 'flex',
            alignItems: 'center',
        },
    };
    const inlineChoiceGroupStyles = {
        root: {
            margin: 0,
            minHeight: 32,
            display: 'flex',
            alignItems: 'center',
        },
        flexContainer: {
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            flexWrap: 'wrap',
        },
    };

    return (
        <div
            style={{
                flex: 1,
                minWidth: 0,
                border: '1px solid #e1dfdd',
                borderRadius: 8,
                padding: 16,
                background: '#ffffff',
            }}
        >
            <Stack tokens={{ childrenGap: 16 }}>
                <Stack horizontal horizontalAlign="space-between" verticalAlign="center" wrap tokens={{ childrenGap: 8 }}>
                    <Text variant="xLarge">
                        {mode === 'create'
                            ? `Create ${isJobSection ? 'job definition' : isAgentSection ? 'agent' : isPluginSection ? 'plugin' : isPipelineSection ? 'pipeline' : isExtractionSection ? 'extraction rule' : isSnippetSection ? 'snippet' : 'artifact'}`
                            : selectedName}
                    </Text>
                    <Stack horizontal tokens={{ childrenGap: 8 }}>
                        {supportsSnippetTools && hasSnippetPreview && (
                            <DefaultButton
                                text={previewLoading ? 'Previewing...' : 'Preview'}
                                onClick={onPreview}
                                disabled={previewLoading}
                            />
                        )}
                        {isJobSection && mode !== 'create' && (
                            <DefaultButton
                                text="Duplicate"
                                onClick={onOpenDuplicate}
                            />
                        )}
                        <PrimaryButton
                            text={saving ? 'Saving...' : mode === 'create' ? 'Create' : 'Save'}
                            onClick={onSave}
                            disabled={saving}
                        />
                    </Stack>
                </Stack>

                {detailError && (
                    <MessageBar messageBarType={MessageBarType.error}>{detailError}</MessageBar>
                )}
                {saveError && (
                    <MessageBar messageBarType={MessageBarType.error}>{saveError}</MessageBar>
                )}
                {saveSuccess && (
                    <MessageBar messageBarType={MessageBarType.success}>{saveSuccess}</MessageBar>
                )}
                {previewError && (
                    <MessageBar messageBarType={MessageBarType.error}>{previewError}</MessageBar>
                )}
                {snippetValidationError && (
                    <MessageBar messageBarType={MessageBarType.error}>{snippetValidationError}</MessageBar>
                )}

                {detailLoading ? (
                    <Spinner size={SpinnerSize.large} label="Loading editor..." />
                ) : (
                    <>
                        {isJobSection ? (
                            <>
                                <Stack horizontal wrap tokens={{ childrenGap: 12 }} styles={{ root: { rowGap: 12 } }}>
                                    <div style={{ minWidth: 280 }}>
                                        <TextField
                                            label="Name"
                                            value={jobForm.name}
                                            onChange={(_, value) => {
                                                setJobForm(current => ({ ...current, name: value ?? '' }));
                                            }}
                                            disabled={mode !== 'create'}
                                        />
                                    </div>
                                    <div style={{ minWidth: 220 }}>
                                        <Dropdown
                                            label="Task type"
                                            options={TASK_TYPES}
                                            selectedKey={jobForm.taskType}
                                            onChange={(_, option) => {
                                                setJobForm(current => ({ ...current, taskType: Number(option?.key ?? 0) }));
                                            }}
                                        />
                                    </div>
                                </Stack>

                                <Stack tokens={{ childrenGap: 12 }}>
                                    <Stack horizontal wrap verticalAlign="center" tokens={{ childrenGap: 24 }} styles={{ root: { rowGap: 12 } }}>
                                        <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 12 }}>
                                            <Label styles={inlineChoiceGroupLabelStyles}>
                                                Execution backend
                                            </Label>
                                            <ChoiceGroup
                                                aria-label="Execution backend"
                                                options={executionBackendOptions}
                                                selectedKey={String(jobForm.executionBackend ?? 0)}
                                                onChange={(_, option) => {
                                                    const nextExecutionBackend = Number(option?.key ?? 0);
                                                    setJobForm(current => ({
                                                        ...current,
                                                        executionBackend: nextExecutionBackend,
                                                        effectiveMaxConcurrency: current.maxConcurrency.trim().length > 0
                                                            ? (Number.isInteger(Number(current.maxConcurrency)) && Number(current.maxConcurrency) > 0
                                                                ? Number(current.maxConcurrency)
                                                                : current.effectiveMaxConcurrency)
                                                            : getDefaultMaxConcurrencyForBackend(nextExecutionBackend),
                                                    }));
                                                }}
                                                styles={inlineChoiceGroupStyles}
                                            />
                                        </Stack>
                                    </Stack>
                                    <Stack horizontal wrap tokens={{ childrenGap: 16 }} styles={{ root: { rowGap: 8 } }}>
                                        <Checkbox
                                            label="Creates PRs"
                                            checked={jobForm.canCreatePR}
                                            onChange={(_, checked) => {
                                                const canCreatePR = !!checked;
                                                setJobForm(current => ({
                                                    ...current,
                                                    canCreatePR,
                                                    canPublishPR: canCreatePR ? current.canPublishPR : false,
                                                }));
                                            }}
                                        />
                                        <Checkbox
                                            label="Can publish PR"
                                            checked={jobForm.canPublishPR}
                                            disabled={!jobForm.canCreatePR}
                                            onChange={(_, checked) => {
                                                setJobForm(current => ({
                                                    ...current,
                                                    canPublishPR: current.canCreatePR ? !!checked : false,
                                                }));
                                            }}
                                        />
                                    </Stack>
                                </Stack>
                                <div style={{ maxWidth: 420 }}>
                                    <Dropdown
                                        label="Agent"
                                        options={selectedAgentOptions}
                                        selectedKey={jobForm.agentArtifactId}
                                        onChange={(_, option) => {
                                            const nextAgentArtifactId = Number(option?.key ?? 0);
                                            setJobForm(current => ({
                                                ...current,
                                                agentArtifactId: nextAgentArtifactId > 0 ? nextAgentArtifactId : undefined,
                                            }));
                                        }}
                                        placeholder="Optional"
                                    />
                                </div>
                                {isAgencyBackend && (
                                    <div style={{ maxWidth: 420 }}>
                                        <Dropdown
                                            label="Pipeline"
                                            options={selectedPipelineOptions}
                                            selectedKey={jobForm.pipelineArtifactId ?? 0}
                                            onChange={(_, option) => {
                                                const nextPipelineArtifactId = Number(option?.key ?? 0);
                                                setJobForm(current => ({
                                                    ...current,
                                                    pipelineArtifactId: nextPipelineArtifactId > 0 ? nextPipelineArtifactId : undefined,
                                                }));
                                            }}
                                        />
                                    </div>
                                )}
                                <div style={{ maxWidth: 420 }}>
                                    <Dropdown
                                        label="Depends on"
                                        options={dependsOnOptions}
                                        selectedKey={jobForm.dependsOnJobDefinitionId}
                                        onChange={(_, option) => {
                                            const nextDependsOnJobDefinitionId = Number(option?.key ?? 0);
                                            setJobForm(current => ({
                                                ...current,
                                                dependsOnJobDefinitionId: nextDependsOnJobDefinitionId > 0 ? nextDependsOnJobDefinitionId : undefined,
                                            }));
                                        }}
                                        placeholder="Optional"
                                    />
                                </div>
                                <div style={{ maxWidth: 220 }}>
                                    <TextField
                                        label="Max concurrency"
                                        value={jobForm.maxConcurrency}
                                        type="number"
                                        placeholder={`Default (${jobForm.effectiveMaxConcurrency})`}
                                        onChange={(_, value) => {
                                            const nextValue = value ?? '';
                                            setJobForm(current => ({
                                                ...current,
                                                maxConcurrency: nextValue,
                                                effectiveMaxConcurrency: nextValue.trim().length > 0
                                                    && Number.isInteger(Number(nextValue))
                                                    && Number(nextValue) > 0
                                                    ? Number(nextValue)
                                                    : getDefaultMaxConcurrencyForBackend(current.executionBackend ?? 0),
                                            }));
                                        }}
                                    />
                                </div>
                                {isAgencyBackend && (
                                    <div style={{ border: '1px solid #e1dfdd', borderRadius: 6, padding: 12, maxWidth: 420 }}>
                                        <Stack tokens={{ childrenGap: 12 }}>
                                            <Text variant="mediumPlus">Model configuration</Text>
                                            {agencyModelsMessage && (
                                                <MessageBar messageBarType={MessageBarType.warning}>
                                                    {agencyModelsMessage}
                                                </MessageBar>
                                            )}
                                            <AgencyModelConfigurationEditor
                                                value={jobForm.modelConfiguration}
                                                modelOptions={agencyModelOptions}
                                                disabled={saving || agencyModelsLoading}
                                                hideModelLabel={true}
                                                onChange={(modelConfiguration) => {
                                                    setJobForm(current => ({
                                                        ...current,
                                                        modelConfiguration,
                                                    }));
                                                }}
                                            />
                                        </Stack>
                                    </div>
                                )}
                                {mode !== 'create' && (
                                    <Checkbox
                                        label="Disabled"
                                        checked={jobForm.disabled}
                                        onChange={(_, checked) => {
                                            setJobForm(current => ({ ...current, disabled: !!checked }));
                                        }}
                                    />
                                )}

                                {isAgencyBackend && (
                                    <DefinitionRelationshipsEditor
                                        title="Plugins"
                                        emptyText="No related plugins."
                                        addLabel="Add plugin"
                                        addButtonText="Add plugin"
                                        emphasizeRelatedArtifacts={true}
                                        showArtifactTypeLabel={false}
                                        relatedArtifacts={pluginArtifacts}
                                        relationshipOptions={pluginRelationshipOptions}
                                        relationshipToAddId={pluginRelationshipToAddId}
                                        onRelationshipToAddChange={onPluginRelationshipToAddIdChange}
                                        onAddRelationship={onAddPluginRelationship}
                                        onRemoveRelationship={onRemovePluginRelationship}
                                    />
                                )}

                                {isAgencyAnalysisJob && (
                                    <DefinitionRelationshipsEditor
                                        title="Extraction rules"
                                        emptyText="No related extraction rules."
                                        addLabel="Add extraction rule"
                                        addButtonText="Add rule"
                                        emphasizeRelatedArtifacts={true}
                                        showArtifactTypeLabel={false}
                                        relatedArtifacts={extractionArtifacts}
                                        relationshipOptions={extractionRelationshipOptions}
                                        relationshipToAddId={extractionRelationshipToAddId}
                                        onRelationshipToAddChange={onExtractionRelationshipToAddIdChange}
                                        onAddRelationship={onAddExtractionRelationship}
                                        onRemoveRelationship={onRemoveExtractionRelationship}
                                    />
                                )}

                                {!isAgencyBackend && (
                                    <DefinitionRelationshipsEditor
                                        title="Skills and scripts"
                                        emptyText="No related skills or scripts."
                                        addLabel="Add skill or script"
                                        addButtonText="Add relationship"
                                        relatedArtifacts={relatedArtifacts}
                                        relationshipOptions={relationshipOptions}
                                        relationshipToAddId={relationshipToAddId}
                                        onRelationshipToAddChange={onRelationshipToAddIdChange}
                                        onAddRelationship={onAddRelationship}
                                        onRemoveRelationship={onRemoveRelationship}
                                    />
                                )}
                            </>
                        ) : isAgentSection ? (
                            <>
                                <Text styles={{ root: { color: '#605e5c' } }}>
                                    The artifact name identifies the reusable definition in this service. The runtime name is the Agency custom agent name or spec-encoded reference.
                                </Text>
                                <Stack horizontal wrap tokens={{ childrenGap: 12 }} styles={{ root: { rowGap: 12 } }}>
                                    <div style={{ minWidth: 280 }}>
                                        <TextField
                                            label="Artifact name"
                                            value={agentForm.name}
                                            onChange={(_, value) => {
                                                setAgentForm(current => ({ ...current, name: value ?? '' }));
                                            }}
                                            disabled={mode !== 'create'}
                                        />
                                    </div>
                                    <div style={{ minWidth: 280 }}>
                                        <TextField
                                            label="Runtime name"
                                            value={agentForm.runtimeName}
                                            onChange={(_, value) => {
                                                setAgentForm(current => ({ ...current, runtimeName: value ?? '' }));
                                            }}
                                        />
                                    </div>
                                    {agentForm.selectedPluginArtifactId === undefined && (
                                        <div style={{ minWidth: 220 }}>
                                            <Dropdown
                                                label="Resolution scope"
                                                options={AGENCY_ARTIFACT_RESOLUTION_TYPE_OPTIONS}
                                                selectedKey={agentForm.resolutionType}
                                                onChange={(_, option) => {
                                                    setAgentForm(current => ({
                                                        ...current,
                                                        resolutionType: String(option?.key ?? 'Company') as AgencyArtifactResolutionType,
                                                    }));
                                                }}
                                            />
                                        </div>
                                    )}
                                </Stack>
                                <div style={{ maxWidth: 420 }}>
                                    <Dropdown
                                        label="Bundled plugin"
                                        options={bundledPluginOptions}
                                        selectedKey={agentForm.selectedPluginArtifactId ?? 0}
                                        onChange={(_, option) => {
                                            const nextPluginArtifactId = Number(option?.key ?? 0);
                                            const optionData = option?.data as { resolutionType?: AgencyArtifactResolutionType } | undefined;
                                            setAgentForm(current => ({
                                                ...current,
                                                selectedPluginArtifactId: nextPluginArtifactId > 0 ? nextPluginArtifactId : undefined,
                                                pluginName: '',
                                                unmatchedPluginReference: undefined,
                                                unmatchedPluginResolutionType: undefined,
                                                resolutionType: nextPluginArtifactId > 0
                                                    ? optionData?.resolutionType ?? current.resolutionType
                                                    : 'Company',
                                            }));
                                        }}
                                    />
                                    <Text block styles={{ root: { color: '#605e5c', marginTop: 4 } }}>
                                        {agentForm.unmatchedPluginReference
                                            ? `Stored bundled plugin '${agentForm.unmatchedPluginReference}' does not match a current Plugin artifact. Select a plugin to replace it.`
                                            : 'Optional. Resolution scope comes from the selected Plugin definition. The implied plugin is not added to the job plugin list.'}
                                    </Text>
                                </div>
                                {mode !== 'create' && (
                                    <Checkbox
                                        label="Disabled"
                                        checked={agentForm.disabled}
                                        onChange={(_, checked) => {
                                            setAgentForm(current => ({ ...current, disabled: !!checked }));
                                        }}
                                    />
                                )}
                                <div style={{ border: '1px solid #e1dfdd', borderRadius: 6, padding: 12 }}>
                                    <Stack tokens={{ childrenGap: 12 }}>
                                        <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
                                            <Text variant="mediumPlus">Agent inputs</Text>
                                            <DefaultButton
                                                text="Add input"
                                                onClick={() => {
                                                    setAgentForm(current => ({
                                                        ...current,
                                                        inputs: [...current.inputs, { key: '', value: '' }],
                                                    }));
                                                }}
                                            />
                                        </Stack>
                                        {agentForm.inputs.length === 0 ? (
                                            <Text>No inputs configured.</Text>
                                        ) : (
                                            <Stack tokens={{ childrenGap: 8 }}>
                                                {agentForm.inputs.map((input, index) => (
                                                    <Stack key={`${index}-${input.key}`} horizontal wrap verticalAlign="end" tokens={{ childrenGap: 8 }}>
                                                        <div style={{ minWidth: 200, flex: 1 }}>
                                                            <TextField
                                                                label="Key"
                                                                value={input.key}
                                                                onChange={(_, value) => {
                                                                    setAgentForm(current => ({
                                                                        ...current,
                                                                        inputs: current.inputs.map((candidate, candidateIndex) => candidateIndex === index
                                                                            ? { ...candidate, key: value ?? '' }
                                                                            : candidate),
                                                                    }));
                                                                }}
                                                            />
                                                        </div>
                                                        <div style={{ minWidth: 260, flex: 2 }}>
                                                            <TextField
                                                                label="Value"
                                                                value={input.value}
                                                                onChange={(_, value) => {
                                                                    setAgentForm(current => ({
                                                                        ...current,
                                                                        inputs: current.inputs.map((candidate, candidateIndex) => candidateIndex === index
                                                                            ? { ...candidate, value: value ?? '' }
                                                                            : candidate),
                                                                    }));
                                                                }}
                                                            />
                                                        </div>
                                                        <IconButton
                                                            iconProps={{ iconName: 'Delete' }}
                                                            title="Remove input"
                                                            ariaLabel="Remove input"
                                                            onClick={() => {
                                                                setAgentForm(current => ({
                                                                    ...current,
                                                                    inputs: current.inputs.filter((_, candidateIndex) => candidateIndex !== index),
                                                                }));
                                                            }}
                                                        />
                                                    </Stack>
                                                ))}
                                            </Stack>
                                        )}
                                    </Stack>
                                </div>
                            </>
                        ) : isPluginSection ? (
                            <>
                                <Text styles={{ root: { color: '#605e5c' } }}>
                                    The artifact name identifies the reusable definition in this service.
                                </Text>
                                <Stack horizontal wrap tokens={{ childrenGap: 12 }} styles={{ root: { rowGap: 12 } }}>
                                    <div style={{ minWidth: 280 }}>
                                        <TextField
                                            label="Artifact name"
                                            value={pluginForm.name}
                                            onChange={(_, value) => {
                                                setPluginForm(current => ({ ...current, name: value ?? '' }));
                                            }}
                                            disabled={mode !== 'create'}
                                        />
                                    </div>
                                    <div style={{ minWidth: 220 }}>
                                        <Dropdown
                                            label="Resolution scope"
                                            options={AGENCY_ARTIFACT_RESOLUTION_TYPE_OPTIONS}
                                            selectedKey={pluginForm.resolutionType}
                                            onChange={(_, option) => {
                                                const nextResolutionType = String(option?.key ?? 'Company') as AgencyArtifactResolutionType;
                                                setPluginForm(current => ({
                                                    ...current,
                                                    resolutionType: nextResolutionType,
                                                    specSegments: nextResolutionType === 'Spec'
                                                        ? AGENCY_SPEC_FIELD_DESCRIPTORS[current.specFormat].map(() => '')
                                                        : current.specSegments,
                                                }));
                                            }}
                                        />
                                    </div>
                                </Stack>
                                {pluginForm.resolutionType === 'Spec' ? (
                                    <div style={{ border: '1px solid #e1dfdd', borderRadius: 6, padding: 12 }}>
                                        <Stack tokens={{ childrenGap: 12 }}>
                                            <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 12 }}>
                                                <Label styles={inlineChoiceGroupLabelStyles}>
                                                    Spec format
                                                </Label>
                                                <ChoiceGroup
                                                    aria-label="Spec format"
                                                    options={specFormatOptions}
                                                    selectedKey={pluginForm.specFormat}
                                                    onChange={(_, option) => {
                                                        const nextFormat = String(option?.key ?? 'market') as typeof pluginForm.specFormat;
                                                        setPluginForm(current => ({
                                                            ...current,
                                                            specFormat: nextFormat,
                                                            specSegments: AGENCY_SPEC_FIELD_DESCRIPTORS[nextFormat].map(() => ''),
                                                        }));
                                                    }}
                                                    styles={inlineChoiceGroupStyles}
                                                />
                                            </Stack>
                                            {pluginSpecWarning && (
                                                <Text styles={{ root: { color: '#8a8886' } }}>{pluginSpecWarning}</Text>
                                            )}
                                            <Stack tokens={{ childrenGap: 8 }}>
                                                {pluginSpecFieldDescriptors.map((descriptor, index) => (
                                                    <Stack key={`plugin-spec-segment-${descriptor.key}`} horizontal wrap verticalAlign="end" tokens={{ childrenGap: 8 }}>
                                                        <div style={{ minWidth: 260, flex: 1 }}>
                                                            <TextField
                                                                label={descriptor.optional ? `${descriptor.label} (optional)` : descriptor.label}
                                                                value={pluginForm.specSegments[index] ?? ''}
                                                                onChange={(_, value) => {
                                                                    const nextValue = descriptor.key === 'path'
                                                                        ? (value ?? '').replace(/^\/+/, '')
                                                                        : (value ?? '');
                                                                    setPluginForm(current => ({
                                                                        ...current,
                                                                        specSegments: current.specSegments.map((candidate, candidateIndex) => candidateIndex === index
                                                                            ? nextValue
                                                                            : candidate),
                                                                    }));
                                                                }}
                                                            />
                                                        </div>
                                                    </Stack>
                                                ))}
                                            </Stack>
                                            <TextField
                                                label="Encoded runtime name"
                                                value={pluginSpecPreview}
                                                readOnly
                                            />
                                        </Stack>
                                    </div>
                                ) : (
                                    <TextField
                                        label="Runtime name"
                                        value={pluginForm.runtimeName}
                                        onChange={(_, value) => {
                                            setPluginForm(current => ({ ...current, runtimeName: value ?? '' }));
                                        }}
                                    />
                                )}
                                {mode !== 'create' && (
                                    <Checkbox
                                        label="Disabled"
                                        checked={pluginForm.disabled}
                                        onChange={(_, checked) => {
                                            setPluginForm(current => ({ ...current, disabled: !!checked }));
                                        }}
                                    />
                                )}
                            </>
                        ) : isPipelineSection ? (
                            <>
                                <Text styles={{ root: { color: '#605e5c' } }}>
                                    The artifact name identifies a reusable Agency pipeline compute definition in this service.
                                </Text>
                                <Stack horizontal wrap tokens={{ childrenGap: 12 }} styles={{ root: { rowGap: 12 } }}>
                                    <div style={{ minWidth: 280 }}>
                                        <TextField
                                            label="Artifact name"
                                            value={pipelineForm.name}
                                            onChange={(_, value) => {
                                                setPipelineForm(current => ({ ...current, name: value ?? '' }));
                                            }}
                                            disabled={mode !== 'create'}
                                        />
                                    </div>
                                    <div style={{ minWidth: 220 }}>
                                        <TextField
                                            label="PipelineId"
                                            value={pipelineForm.pipelineId}
                                            onChange={(_, value) => {
                                                setPipelineForm(current => ({ ...current, pipelineId: value ?? '' }));
                                            }}
                                        />
                                    </div>
                                    <div style={{ minWidth: 280 }}>
                                        <TextField
                                            label="ProjectId"
                                            value={pipelineForm.projectId}
                                            onChange={(_, value) => {
                                                setPipelineForm(current => ({ ...current, projectId: value ?? '' }));
                                            }}
                                        />
                                    </div>
                                    <div style={{ minWidth: 280 }}>
                                        <TextField
                                            label="PipelineRefOverride"
                                            value={pipelineForm.pipelineRefOverride}
                                            onChange={(_, value) => {
                                                setPipelineForm(current => ({ ...current, pipelineRefOverride: value ?? '' }));
                                            }}
                                        />
                                    </div>
                                </Stack>
                                {mode !== 'create' && (
                                    <Checkbox
                                        label="Disabled"
                                        checked={pipelineForm.disabled}
                                        onChange={(_, checked) => {
                                            setPipelineForm(current => ({ ...current, disabled: !!checked }));
                                        }}
                                    />
                                )}
                            </>
                        ) : isExtractionSection ? (
                            <>
                                <Text styles={{ root: { color: '#605e5c' } }}>
                                    Extraction rules parse Agency job transcripts after analysis jobs complete and store the captured text into work-item properties such as EstimatedGain, Output1, or Output2.
                                </Text>
                                <Stack horizontal wrap tokens={{ childrenGap: 12 }} styles={{ root: { rowGap: 12 } }}>
                                    <div style={{ minWidth: 280 }}>
                                        <TextField
                                            label="Artifact name"
                                            value={extractionForm.name}
                                            onChange={(_, value) => {
                                                setExtractionForm(current => ({ ...current, name: value ?? '' }));
                                            }}
                                            disabled={mode !== 'create'}
                                        />
                                    </div>
                                    <div style={{ minWidth: 220 }}>
                                        <Dropdown
                                            label="Result type"
                                            options={JOB_RESULT_EXTRACTION_PURPOSE_OPTIONS}
                                            selectedKey={extractionForm.purpose}
                                            onChange={(_, option) => {
                                                setExtractionForm(current => ({
                                                    ...current,
                                                    purpose: String(option?.key ?? 'GenericOutput') as ExtractionArtifactEditorState['purpose'],
                                                }));
                                            }}
                                        />
                                        <Text variant="small" styles={{ root: { color: '#605e5c' } }}>
                                            Controls how Guardian interprets and persists the extracted value.
                                        </Text>
                                    </div>
                                    <div style={{ minWidth: 240 }}>
                                        <Dropdown
                                            label="Source"
                                            options={JOB_RESULT_EXTRACTION_SOURCE_OPTIONS}
                                            selectedKey={extractionForm.source}
                                            onChange={(_, option) => {
                                                setExtractionForm(current => ({
                                                    ...current,
                                                    source: String(option?.key ?? 'LastAssistantText') as ExtractionArtifactEditorState['source'],
                                                }));
                                            }}
                                        />
                                        <Text variant="small" styles={{ root: { color: '#605e5c' } }}>
                                            Assistant messages uses only assistant-message content; combined sources concatenate matching transcript content.
                                        </Text>
                                    </div>
                                    <div style={{ minWidth: 220 }}>
                                        <Dropdown
                                            label="Mode"
                                            options={JOB_RESULT_EXTRACTION_MODE_OPTIONS}
                                            selectedKey={extractionForm.mode}
                                            onChange={(_, option) => {
                                                const nextMode = String(option?.key ?? 'Regex') as ExtractionArtifactEditorState['mode'];
                                                setExtractionForm(current => ({
                                                    ...current,
                                                    mode: nextMode,
                                                }));
                                            }}
                                        />
                                    </div>
                                    <div style={{ minWidth: 220 }}>
                                        <Dropdown
                                            label="Destination property"
                                            options={JOB_RESULT_EXTRACTION_DESTINATION_OPTIONS}
                                            selectedKey={extractionForm.destination}
                                            onChange={(_, option) => {
                                                setExtractionForm(current => ({
                                                    ...current,
                                                    destination: String(option?.key ?? 'Output1') as ExtractionArtifactEditorState['destination'],
                                                }));
                                            }}
                                        />
                                    </div>
                                </Stack>
                                {extractionForm.mode === 'Regex' ? (
                                    <>
                                        <TextField
                                            label="Regex pattern"
                                            value={extractionForm.regexPattern}
                                            onChange={(_, value) => {
                                                setExtractionForm(current => ({ ...current, regexPattern: value ?? '' }));
                                            }}
                                        />
                                        <Stack horizontal wrap tokens={{ childrenGap: 16 }} styles={{ root: { rowGap: 8 } }}>
                                            <Checkbox
                                                label="Ignore case"
                                                checked={extractionForm.ignoreCase}
                                                onChange={(_, checked) => {
                                                    setExtractionForm(current => ({ ...current, ignoreCase: !!checked }));
                                                }}
                                            />
                                            <Checkbox
                                                label="Multiline"
                                                checked={extractionForm.multiline}
                                                onChange={(_, checked) => {
                                                    setExtractionForm(current => ({ ...current, multiline: !!checked }));
                                                }}
                                            />
                                            <Checkbox
                                                label="Singleline"
                                                checked={extractionForm.singleline}
                                                onChange={(_, checked) => {
                                                    setExtractionForm(current => ({ ...current, singleline: !!checked }));
                                                }}
                                            />
                                        </Stack>
                                    </>
                                ) : (
                                    <Stack horizontal wrap tokens={{ childrenGap: 12 }} styles={{ root: { rowGap: 12 } }}>
                                        <div style={{ minWidth: 320, flex: 1 }}>
                                            <TextField
                                                label="Start delimiter"
                                                value={extractionForm.startDelimiter}
                                                onChange={(_, value) => {
                                                    setExtractionForm(current => ({ ...current, startDelimiter: value ?? '' }));
                                                }}
                                            />
                                        </div>
                                        <div style={{ minWidth: 320, flex: 1 }}>
                                            <TextField
                                                label="End delimiter"
                                                value={extractionForm.endDelimiter}
                                                onChange={(_, value) => {
                                                    setExtractionForm(current => ({ ...current, endDelimiter: value ?? '' }));
                                                }}
                                                description="Optional. Leave empty to capture everything after the start delimiter."
                                            />
                                        </div>
                                    </Stack>
                                )}
                                {mode !== 'create' && (
                                    <Checkbox
                                        label="Disabled"
                                        checked={extractionForm.disabled}
                                        onChange={(_, checked) => {
                                            setExtractionForm(current => ({ ...current, disabled: !!checked }));
                                        }}
                                    />
                                )}
                            </>
                        ) : (
                            <>
                                <Stack horizontal wrap tokens={{ childrenGap: 12 }} styles={{ root: { rowGap: 12 } }}>
                                    <div style={{ minWidth: 280 }}>
                                        <TextField
                                            label="Name"
                                            value={artifactForm.name}
                                            onChange={(_, value) => {
                                                setArtifactForm(current => ({ ...current, name: value ?? '' }));
                                            }}
                                            disabled={mode !== 'create'}
                                        />
                                    </div>
                                    <div style={{ minWidth: 220 }}>
                                        {isSnippetSection ? (
                                            <TextField label="Type" value="Snippet" readOnly />
                                        ) : (
                                            <Dropdown
                                                label="Type"
                                                options={ARTIFACT_TYPE_OPTIONS}
                                                selectedKey={artifactForm.type}
                                                onChange={(_, option) => {
                                                    setArtifactForm(current => ({ ...current, type: Number(option?.key ?? 1) }));
                                                }}
                                                disabled={mode !== 'create'}
                                            />
                                        )}
                                    </div>
                                </Stack>
                                {mode !== 'create' && (
                                    <Checkbox
                                        label="Disabled"
                                        checked={artifactForm.disabled}
                                        onChange={(_, checked) => {
                                            setArtifactForm(current => ({ ...current, disabled: !!checked }));
                                        }}
                                    />
                                )}
                                {isSnippetSection && (
                                    <Text styles={{ root: { color: '#605e5c' } }}>
                                        Disabled snippets stay available for preview and runtime expansion, but they are hidden from insertion and relationship pickers.
                                    </Text>
                                )}
                            </>
                        )}

                        {supportsSnippetTools && (
                            <SnippetTools
                                enabledSnippets={enabledSnippets}
                                selectedSnippetName={selectedSnippetName}
                                onSelectedSnippetNameChange={onSelectedSnippetNameChange}
                                availableSubstitutions={availableSubstitutions}
                                selectedSubstitutionToken={selectedSubstitutionToken}
                                onSelectedSubstitutionTokenChange={onSelectedSubstitutionTokenChange}
                                selectedInsertionKind={selectedInsertionKind}
                                onSelectedInsertionKindChange={onSelectedInsertionKindChange}
                                onInsert={onInsertSnippet}
                                onInsertSubstitution={onInsertSubstitution}
                            />
                        )}

                        {supportsTextEditing && (
                            <div>
                                <Label>{isJobSection ? 'Prompt' : `${getArtifactTypeLabel(artifactForm.type)} text`}</Label>
                                {isJobSection && jobForm.dependsOnJobDefinitionId && (
                                    <Text styles={{ root: { color: '#605e5c', marginBottom: 8 } }}>
                                        Dependency prompts must include <code>{'{{ UpstreamJob.ProposedChanges }}'}</code> for structured analysis, or <code>{'{{ UpstreamJob.Output }}'}</code> for generic legacy output.
                                    </Text>
                                )}
                                <textarea
                                    ref={editorRef}
                                    value={previewSourceText}
                                    onChange={event => onUpdateEditorText(event.target.value)}
                                    style={{
                                        width: '100%',
                                        minHeight: 360,
                                        padding: 12,
                                        borderRadius: 6,
                                        border: '1px solid #c8c6c4',
                                        fontFamily: 'Consolas, "Courier New", monospace',
                                        fontSize: 13,
                                        lineHeight: 1.5,
                                        resize: 'vertical',
                                    }}
                                />
                            </div>
                        )}

                        {previewText !== null && (
                            <div>
                                <Label>Preview</Label>
                                <TextField value={previewText} multiline rows={12} readOnly />
                            </div>
                        )}
                    </>
                )}
            </Stack>
        </div>
    );
}
